// (C) Copyright 2025 Hewlett Packard Enterprise Development LP

import React, { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Box } from 'grommet'
import { useNavigate, useParams } from 'react-router-dom'
import lodashGet from 'lodash/get'
import { StatusCritical } from 'grommet-icons'

import {
  Notification,
  Anchor,
  Loader,
  Typography,
  Wizard
} from '../../../../../components'
import { validateForm } from '../../../../../utils/validation-utils'
import { getErrorMessage, get, post, put } from '../../../../../utils/api-utils'
import { displayApiError } from '../../../../../utils/error-handling-utils'
import { ssoModeEnum } from '../../constants'

import General from './steps/General'
import {
  validateConfigureSettingsFields,
  validateGeneralFields,
  validateIDPDetails,
  validateTimeoutField
} from './steps/utils/validation-utils'
import ConfigureSettings from './steps/ConfigureSettings'
import ConfigureIdp from './steps/ConfigureIdp'
import AddIDPDetails from './steps/add-idp-details/AddIDPDetails'
import ReviewAndCreate from './steps/ReviewAndCreate'
import Timeout from './steps/Timeout'
import {
  attributeMappingDefaultFormValues,
  defaultGeneral
} from './steps/utils/page-utils'

const AddSsoConnection = () => {
  const navigate = useNavigate()
  const { t } = useTranslation(['common', 'iam'])
  const { id } = useParams()
  const [configValues] = useState(null)
  const [multiDomainErrorInfo, setMultiDomainErrorInfo] = useState({})
  const [disableFinishOnSubmit, setDisableFinishOnSubmit] = useState(true)
  const [errorMsg, setErrorMsg] = useState(null)
  const [profileLoading, setProfileLoading] = useState(false)
  const [routingLoading, setRoutingLoading] = useState(false)
  const [apiErrorNotification, setApiErrorNotification] = useState(null)
  const [profileData, setProfileData] = useState(null)
  const [routingData, setRoutingData] = useState(null)
  const [createdSsoConnectionData, setCreatedSsoConnectionData] = useState(null)
  const [updatedSsoProfileData, setUpdatedSsoProfileData] = useState(null)
  const [initialFormData, setInitialFormData] = useState({})

  const actionOnExitFn = useCallback(() => {
    if (id) {
      if (updatedSsoProfileData) {
        const ssoProfileName = updatedSsoProfileData?.name
        navigate(
          `/manage-account/organization/sso-configurations/sso-connection/${updatedSsoProfileData.id}`,
          {
            notification: t(
              'iam:sso_profiles.view_edit_create.profile_updated',
              {
                ssoProfileName
              }
            )
          }
        )
      } else {
        navigate(
          `/manage-account/organization/sso-configurations/sso-connection/${id}`
        )
      }
    } else if (createdSsoConnectionData) {
      const ssoConnectionName = createdSsoConnectionData?.name
      navigate(`/manage-account/organization/sso-configurations`, {
        state: {
          notification: t(
            'iam:sso_configurations.view_edit_create_connection.connection_created',
            {
              ssoConnectionName
            }
          )
        }
      })
    } else {
      navigate('/manage-account/organization/sso-configurations')
    }
  }, [createdSsoConnectionData, navigate, id, updatedSsoProfileData, t])

  useEffect(() => {
    if (createdSsoConnectionData || updatedSsoProfileData) {
      actionOnExitFn()
    }
  }, [createdSsoConnectionData, updatedSsoProfileData, actionOnExitFn])

  const ssoConnectionRequestBody = (formValues) => {
    const attributeMapping = [
      {
        name: 'Email',
        expression: lodashGet(formValues, 'attribute_mapping.email').toString()
      },
      {
        name: 'FirstName',
        expression: lodashGet(
          formValues,
          'attribute_mapping.firstName'
        ).toString()
      },
      {
        name: 'LastName',
        expression: lodashGet(
          formValues,
          'attribute_mapping.lastName'
        ).toString()
      },
      {
        name: 'IdleSessionTimeout',
        expression: lodashGet(formValues, 'timeout').toString()
      }
    ]

    attributeMapping.push({
      name: 'HPECCSAttribute',
      expression: 'HPE_GreenLake_authorization'
    })

    const body = {
      name: lodashGet(formValues, 'general.connectionName').toString(),
      attributeMapping,
      samlIdpConfig: {
        entityId: lodashGet(formValues, 'saml_idp_config.entity_id'),
        loginUrl: lodashGet(formValues, 'saml_idp_config.login_url'),
        logoutUrl: lodashGet(formValues, 'saml_idp_config.logout_url'),
        certificate: lodashGet(
          formValues,
          'saml_idp_config.signing_certificate'
        )
      }
    }

    return body
  }

  const createSSOConnection = async (
    formValues,
    setFormError,
    i18nTranslate
  ) => {
    try {
      const ssoProfileRequest = ssoConnectionRequestBody(formValues)

      const response = await post(
        '/identity/v2alpha1/sso-profiles',
        ssoProfileRequest
      )

      if (id) {
        setUpdatedSsoProfileData(response.data)
      } else {
        setCreatedSsoConnectionData(response.data)
      }
    } catch (error) {
      const backendErrorMessage = getErrorMessage(error, i18nTranslate)
      setFormError(backendErrorMessage)
      setDisableFinishOnSubmit(false)
    }
  }

  // TBD This basic function will be used later for the update functionality
  const removeAttribute = (obj, attribute) => {
    const { [attribute]: removed, ...rest } = obj
    return rest
  }

  const hasFormChanged = (formData, ignoreAttribute = '') => {
    const filteredFormData = removeAttribute(formData, ignoreAttribute)
    const filteredInitialFormData = removeAttribute(
      initialFormData,
      ignoreAttribute
    )

    return (
      JSON.stringify(filteredFormData) !==
      JSON.stringify(filteredInitialFormData)
    )
  }

  const updateSsoProfile = async (formValues, setFormError, i18nTranslate) => {
    try {
      if (!hasFormChanged(formValues)) {
        setFormError(t('iam:sso_profiles.view_edit_create.no_changes_error'))
        setDisableFinishOnSubmit(false)
        return
      }

      const ssoProfileRequest = ssoConnectionRequestBody(formValues)

      let response = null
      if (hasFormChanged(formValues, 'domain')) {
        response = await put(
          `/identity/v1alpha1/sso-profiles/${id}`,
          ssoProfileRequest
        )
      }

      if (id) {
        setUpdatedSsoProfileData(response.data)
      } else {
        setCreatedSsoConnectionData(response.data)
      }
    } catch (error) {
      const backendErrorMessage = getErrorMessage(error, i18nTranslate)
      setFormError(backendErrorMessage)
      setDisableFinishOnSubmit(false)
    }
  }

  const actionOnSubmit = async (formValues, setFormError, i18nTranslate) => {
    setFormError('')
    if (id) {
      await updateSsoProfile(formValues, setFormError, i18nTranslate)
    } else {
      await createSSOConnection(formValues, setFormError, i18nTranslate)
    }
  }

  const wizardButtons = {
    next: {
      label: t('common:button.next'),
      submitForm: false
    },
    finish: id
      ? t('iam:sso_profiles.view_edit_create.save_changes')
      : t('iam:sso_configurations.create_sso_connection')
  }

  const validateFormData = () => {
    return new Promise((resolve) => {
      resolve()
    })
  }
  const steps = [
    {
      title: t('iam:sso_configurations.view_edit_create_connection.general'),
      childComponents: <General />,
      description: (
        <Typography type="text" size="large" weight={400}>
          <Trans
            i18nKey="iam:sso_configurations.view_edit_create_connection.general_description"
            t={t}
          >
            <Anchor
              testId="general_learn_anchor"
              label={t('common:learn_more')}
              weight="bold"
              onClick={() => dispatchEvent(new Event('context-help'))}
            />
          </Trans>
        </Typography>
      ),
      validateForm: (formValues) => {
        return validateForm(formValues, t, validateGeneralFields)
      }
    },
    {
      title: t('iam:sso_profiles.view_edit_create.map_saml_attributes'),
      description: (
        <Typography type="text" size="large" weight={400}>
          <Trans
            i18nKey="iam:sso_profiles.view_edit_create.map_saml_attributes_description"
            t={t}
          >
            <Anchor
              testId="map_saml_attributes_learn_anchor"
              label={t('common:learn_more')}
              weight="bold"
              onClick={() => dispatchEvent(new Event('context-help'))}
            />
          </Trans>
        </Typography>
      ),
      childComponents: <ConfigureSettings configValues={configValues} />,
      validateForm: (formValues) => {
        return validateForm(formValues, t, validateConfigureSettingsFields)
      }
    },
    {
      title: t(
        'iam:sso_configurations.view_edit_create_connection.configure_idp'
      ),
      description: (
        <Typography type="text" size="large" weight={400}>
          <Trans
            i18nKey="iam:sso_profiles.view_edit_create.configure_idp_description"
            t={t}
          >
            <Anchor
              testId="configure_idp_learn_anchor"
              label={t('common:learn_more')}
              weight="bold"
              onClick={() => dispatchEvent(new Event('context-help'))}
            />
          </Trans>
        </Typography>
      ),
      childComponents: <ConfigureIdp />,
      validateForm: () => validateFormData()
    },
    {
      title: t(
        'iam:sso_configurations.view_edit_create_connection.identity_provider_details'
      ),
      description: (
        <Typography type="text" size="large" weight={400}>
          <Trans
            i18nKey="iam:sso_configurations.view_edit_create_connection.identity_provider_details_description"
            t={t}
          >
            <Anchor
              testId="identity_provider_details_learn_anchor"
              label={t('common:learn_more')}
              weight="bold"
              onClick={() => dispatchEvent(new Event('context-help'))}
            />
          </Trans>
        </Typography>
      ),
      childComponents: (
        <AddIDPDetails
          multiDomainErrorInfo={multiDomainErrorInfo}
          setMultiDomainErrorInfo={setMultiDomainErrorInfo}
        />
      ),
      validateForm: (formValues) => validateIDPDetails(formValues, t)
    },
    {
      title: t(
        'iam:sso_configurations.view_edit_create_connection.session_timeout'
      ),
      description: (
        <Typography type="text" size="large" weight={400}>
          <Trans
            i18nKey="iam:sso_configurations.view_edit_create_connection.session_timeout_description"
            t={t}
          >
            <Anchor
              testId="session_timeout_learn_anchor"
              label={t('common:learn_more')}
              weight="bold"
              onClick={() => dispatchEvent(new Event('context-help'))}
            />
          </Trans>
        </Typography>
      ),
      childComponents: <Timeout />,
      validateForm: (formValues) => {
        return validateForm(formValues, t, validateTimeoutField)
      }
    },
    {
      title: id
        ? t('iam:sso_profiles.view_edit_create.review_save_changes')
        : t('iam:sso_profiles.view_edit_create.review_create'),
      description: t(
        'iam:sso_configurations.view_edit_create_connection.review_description'
      ),
      childComponents: <ReviewAndCreate />,
      validateForm: () => validateFormData()
    }
  ]

  useEffect(() => {
    if (id) {
      setProfileLoading(true)
      setRoutingLoading(true)

      get(`/identity/v1alpha1/sso-profiles/${id}`, {}).then(
        (ssoResponse) => {
          setProfileData(ssoResponse?.data)
          setProfileLoading(false)
        },
        (error) => {
          setProfileLoading(false)
          setApiErrorNotification(
            displayApiError(error, t, setApiErrorNotification)
          )
        }
      )
      get(`/internal-identity/v1alpha1/sso-routing-rules`, {
        'sso-profile-id': id
      }).then(
        (res) => {
          if (res?.data?.items?.length) {
            setRoutingData(res?.data?.items[0])
          }
          setRoutingLoading(false)
        },
        (error) => {
          setRoutingLoading(false)
          setApiErrorNotification(
            displayApiError(error, t, setApiErrorNotification)
          )
        }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, t])

  const getValueFromArrayObj = (key, arr = []) => {
    return arr.find((obj) => obj.name === key)?.expression
  }

  const formDefaultValues = () => {
    const initialData = {
      general: id
        ? {
            profileName: profileData?.name,
            authorizationMethod: getValueFromArrayObj(
              'HPECCSAttribute',
              profileData?.attributeMapping
            )
              ? ssoModeEnum.AUTHORIZATION
              : ssoModeEnum.AUTHENTICATION_ONLY,
            currentAuthorizationMethod: getValueFromArrayObj(
              'HPECCSAttribute',
              profileData?.attributeMapping
            )
              ? ssoModeEnum.AUTHORIZATION
              : ssoModeEnum.AUTHENTICATION_ONLY
          }
        : defaultGeneral.general,
      domain: routingData?.conditions?.domains[0],
      currentDomain: routingData?.conditions?.domains[0],
      attribute_mapping: id
        ? {
            email:
              getValueFromArrayObj('Email', profileData?.attributeMapping) ??
              '',
            firstName:
              getValueFromArrayObj(
                'FirstName',
                profileData?.attributeMapping
              ) ?? '',
            lastName:
              getValueFromArrayObj('LastName', profileData?.attributeMapping) ??
              '',
            ccsAttribute:
              getValueFromArrayObj(
                'HPECCSAttribute',
                profileData?.attributeMapping
              ) ??
              attributeMappingDefaultFormValues.attribute_mapping.ccsAttribute
          }
        : attributeMappingDefaultFormValues.attribute_mapping,
      timeout:
        getValueFromArrayObj(
          'IdleSessionTimeout',
          profileData?.attributeMapping
        ) || 30,
      saml_idp_config: {
        entity_id: profileData?.samlIdpConfig?.entityId,
        login_url: profileData?.samlIdpConfig?.loginUrl,
        logout_url: profileData?.samlIdpConfig?.logoutUrl,
        signing_certificate: profileData?.samlIdpConfig?.certificate,
        metadata_url: '',
        file_info: {},
        config_option: id ? 'Manual entry of configuration details' : ''
      }
    }
    setInitialFormData(initialData)
    return initialData
  }

  return (
    <>
      {id && (routingLoading || profileLoading) ? (
        <Box direction="row" align="center" justify="center" pad="medium">
          <Loader testId="saml-view-loader" />
        </Box>
      ) : (
        <Box>
          <Wizard
            testId="add-sso-connection-wizard"
            title={
              id
                ? t('iam:sso_profiles.view_edit_create.edit_profile')
                : t('iam:sso_configurations.create_sso_connection')
            }
            formDefaultValues={formDefaultValues}
            actionOnSubmit={(formValues, setFormError) => {
              actionOnSubmit(formValues, setFormError, t)
            }}
            disableFinishOnSubmit={disableFinishOnSubmit}
            buttonLabels={wizardButtons}
            actionOnExit={() => actionOnExitFn()}
            cancelStrings={{
              continueLabel: t(
                'iam:sso_profiles.view_edit_create.stay_in_wizard'
              ),
              exitLabel: t('iam:sso_profiles.view_edit_create.leave_wizard'),
              heading: t(
                'iam:sso_profiles.view_edit_create.leave_without_finishing'
              ),
              text: t(
                'iam:sso_configurations.view_edit_create_connection.lose_progress_warning',
                {
                  action: id
                    ? t('iam:sso_profiles.view_edit_create.update')
                    : t('common:create')
                }
              )
            }}
            steps={steps}
          />
          {errorMsg && (
            <Notification
              backgroundColor="status-critical"
              icon={<StatusCritical color="text-strong" />}
              onClose={() => setErrorMsg(null)}
              text={errorMsg}
              testId="api-notification"
              position="top"
            />
          )}
        </Box>
      )}
      {apiErrorNotification}
    </>
  )
}

export default AddSsoConnection
